import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";

/** Known platform hostnames that are NOT store domains */
const PLATFORM_HOSTS = [
  "localhost",
  "127.0.0.1",
  "lovable.app",
  "lovableproject.com",
  "vazoul.com",      // bare root
  "www.vazoul.com",
  "app.vazoul.com",
  "api.vazoul.com",
];

function isPlatformHost(hostname: string): boolean {
  // Exact match
  if (PLATFORM_HOSTS.some(h => hostname === h || hostname.endsWith("." + h))) return true;
  // Lovable preview/published domains
  if (hostname.includes("lovable.app") || hostname.includes("lovableproject.com")) return true;
  return false;
}

interface DomainResolution {
  isStoreDomain: boolean;
  loading: boolean;
  storeId: string | null;
  slug: string | null;
  storeName: string | null;
  error: string | null;
}

/**
 * Detects if the current hostname is a store domain (subdomain or custom).
 * If yes, resolves it to a store_id and homepage slug.
 * 
 * Resolution priority:
 * 1. Client-side: parse hostname, query store_domains directly
 * 2. Fallback: call resolve-store-domain edge function
 */
export function useStoreDomain(): DomainResolution {
  const [state, setState] = useState<DomainResolution>({
    isStoreDomain: false, loading: true, storeId: null, slug: null, storeName: null, error: null,
  });
  const resolvedRef = useRef(false);

  useEffect(() => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;

    const hostname = window.location.hostname;

    // Skip platform hosts immediately
    if (isPlatformHost(hostname)) {
      setState(s => ({ ...s, loading: false, isStoreDomain: false }));
      return;
    }

    (async () => {
      try {
        // Attempt 1: Client-side resolution for *.vazoul.com
        const subMatch = hostname.match(/^([a-z0-9-]+)\.vazoul\.com$/);
        let storeId: string | null = null;

        if (subMatch) {
          const sub = subMatch[1];
          const { data: domainRow } = await supabase
            .from("store_domains")
            .select("store_id")
            .eq("domain_value", sub)
            .eq("domain_type", "subdomain")
            .limit(1)
            .maybeSingle();

          if (domainRow) storeId = domainRow.store_id;
        } else {
          // Custom domain
          const { data: customRow } = await supabase
            .from("store_domains")
            .select("store_id")
            .eq("domain_value", hostname)
            .eq("domain_type", "custom")
            .limit(1)
            .maybeSingle();

          if (customRow) storeId = customRow.store_id;
        }

        if (!storeId) {
          // Attempt 2: Edge function as fallback
          const { data: fnData, error: fnError } = await supabase.functions.invoke(
            "resolve-store-domain",
            { body: { hostname } }
          );

          if (fnError || fnData?.error) {
            setState({
              isStoreDomain: true, loading: false, storeId: null, slug: null,
              storeName: null, error: fnData?.error || "domain_not_found",
            });
            return;
          }

          setState({
            isStoreDomain: true, loading: false,
            storeId: fnData.store_id, slug: fnData.slug,
            storeName: fnData.store_name, error: null,
          });
          return;
        }

        // Resolve homepage
        const { data: homepage } = await supabase
          .from("smart_store_pages")
          .select("slug")
          .eq("store_id", storeId)
          .eq("page_type", "homepage")
          .eq("status", "published")
          .limit(1)
          .maybeSingle();

        const slug = homepage?.slug || null;

        if (!slug) {
          // Fallback: first published page
          const { data: firstPage } = await supabase
            .from("smart_store_pages")
            .select("slug")
            .eq("store_id", storeId)
            .eq("status", "published")
            .order("created_at", { ascending: true })
            .limit(1)
            .maybeSingle();

          if (firstPage) {
            const { data: storeData } = await supabase
              .from("stores").select("store_name").eq("id", storeId).maybeSingle();
            setState({
              isStoreDomain: true, loading: false, storeId, slug: firstPage.slug,
              storeName: storeData?.store_name || null, error: null,
            });
            return;
          }

          setState({
            isStoreDomain: true, loading: false, storeId, slug: null,
            storeName: null, error: "no_published_pages",
          });
          return;
        }

        const { data: storeData } = await supabase
          .from("stores").select("store_name").eq("id", storeId).maybeSingle();

        setState({
          isStoreDomain: true, loading: false, storeId, slug,
          storeName: storeData?.store_name || null, error: null,
        });
      } catch (err) {
        setState({
          isStoreDomain: false, loading: false, storeId: null, slug: null,
          storeName: null, error: "resolution_failed",
        });
      }
    })();
  }, []);

  return state;
}
